<template>
  <PreviewBase
    v-if="type === 'flsaReport'"
    :loading="previewLoading"
  >
    <template #loading>
      <FlsaReportProfilePreviewSkeleton />
    </template>

    <div
      v-if="profileItem"
      class="flsa-report-profile-preview d-flex flex-column ga-8"
    >
      <FlsaReportPreviewMetadata
        :profile="profile"
        :profile-source="resolvedProfileSource"
      />

      <FlsaReportPreviewAlerts
        :report-item="profileItem"
        :latest-federal-submission="federalSubmissionSummary"
        :forms="forms"
        :job-profile-ulid="submissionProfileUlids.jobProfileUlid"
        :draft-job-profile-ulid="submissionProfileUlids.draftJobProfileUlid"
      />

      <section class="flsa-report-preview-submissions">
        <h2 class="text-h2 text-textDarkest mb-1">
          Existing FLSA Submissions
        </h2>

        <div
          v-if="!federalSubmissions.length"
          class="text-body-2 text-textLight py-4"
        >
          No Federal FLSA submissions yet for this job profile.
        </div>

        <FlsaSubmissionTimeline
          v-else
          :submissions="federalSubmissions"
          show-current-version-badge
          @submission-click="handleSubmissionClick"
        />
      </section>
    </div>

    <div
      v-else-if="!submissionsLoading"
      class="text-body-2 text-textLight"
    >
      Job profile not found in the current report view.
    </div>
  </PreviewBase>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { snakeCase } from 'lodash-es';
import PreviewBase from '@/components/shared/PreviewBase.vue';
import FlsaReportPreviewMetadata from '@/components/library/flsaSurveys/FlsaReportPreviewMetadata.vue';
import FlsaReportPreviewAlerts from '@/components/library/flsaSurveys/FlsaReportPreviewAlerts.vue';
import FlsaReportProfilePreviewSkeleton from '@/components/library/flsaSurveys/FlsaReportProfilePreviewSkeleton.vue';
import FlsaSubmissionTimeline from '@/components/jobProfiles/editor/FlsaSubmissionTimeline.vue';
import { useFlsaSurveysStore } from '@/stores/jobProfiles/flsaSurveys';
import { getFlsaForms, getFlsaSubmissions } from '@/api/jobProfile';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { attachFormYears, getFlsaSubmissionProfileUlids } from '@/helpers/flsa';
import { getFederalLatestSubmission } from '@/components/library/flsaSurveys/helpers';

const props = defineProps({
  profileSource: {
    type: String,
    default: 'hris',
    validator: (value) => ['hris', 'draft'].includes(value),
  },
});

const route = useRoute();
const router = useRouter();

const { getProfileItemByUlid } = useFlsaSurveysStore();

const forms = ref([]);
const federalSubmissions = ref([]);
const submissionsLoading = ref(false);

const type = computed(() => route.query?.type);
const previewUlid = computed(() => route.query?.preview);

const profileItem = computed(() => (
  previewUlid.value ? getProfileItemByUlid(previewUlid.value) : null
));

const profile = computed(() => profileItem.value?.profile || {});

const resolvedProfileSource = computed(() => {
  if (profile.value?.entity_type === 'draft_profile') return 'draft';
  if (profile.value?.entity_type === 'job_profile') return 'hris';
  return props.profileSource;
});

const federalSubmissionSummary = computed(() => {
  const summary = getFederalLatestSubmission(profileItem.value);

  if (!summary) return null;

  return {
    ...summary,
    is_exempt: summary.is_flsa_exempt,
  };
});

const submissionProfileUlids = computed(() => {
  const { jobProfileUlid, draftJobProfileUlid } = getFlsaSubmissionProfileUlids({
    jobProfile: profile.value,
    isHrisProfile: resolvedProfileSource.value === 'hris',
  });

  return { jobProfileUlid, draftJobProfileUlid };
});

const previewLoading = computed(() => submissionsLoading.value);

const loadPreviewData = async () => {
  if (type.value !== 'flsaReport' || !previewUlid.value) {
    forms.value = [];
    federalSubmissions.value = [];

    return;
  }

  const { jobProfileUlid, draftJobProfileUlid } = submissionProfileUlids.value;

  if (!jobProfileUlid && !draftJobProfileUlid) {
    forms.value = [];
    federalSubmissions.value = [];

    return;
  }

  submissionsLoading.value = true;

  try {
    const [formsData, subsData] = await Promise.all([
      getFlsaForms(),
      getFlsaSubmissions({
        jobProfileUlid: jobProfileUlid || undefined,
        draftJobProfileUlid: draftJobProfileUlid || undefined,
      }),
    ]);

    forms.value = formsData;

    const withFormYears = attachFormYears(subsData.items || [], formsData);

    federalSubmissions.value = withFormYears.filter(
      (submission) => snakeCase(submission.jurisdiction) === 'federal',
    );
  } catch (error) {
    forms.value = [];
    federalSubmissions.value = [];

    useAppSnackbar().addErrorSnackbar({
      text: `Failed to load FLSA submissions — ${error.message}`,
    });

    throw error;
  } finally {
    submissionsLoading.value = false;
  }
};

const handleSubmissionClick = (submission) => {
  router.push({ name: 'flsa-form-view', params: { submissionUlid: submission.ulid } });
};

watch(
  () => [type.value, previewUlid.value, profile.value?.ulid],
  () => {
    loadPreviewData();
  },
  { immediate: true },
);
</script>

<style scoped lang="less">
.flsa-report-profile-preview {
  padding-bottom: 32px;
}

.flsa-report-preview-submissions {
  :deep(.flsa-submission-timeline) {
    transform: translateX(-24px);
    width: calc(100% + 48px) !important;
  }
}
</style>
